import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ShopList, { ShopListRef } from '../components/ShopList';
import FeedList, { FeedListRef } from '../components/FeedList';
import NetworkProductsManager, { NetworkProductsManagerRef } from '../components/NetworkProductsManager';

// THE FEEDS HUB. One list, and named doors to everything else — ABOVE it.
//
// WHY THE DOORS ARE ABOVE THE LIST, WHICH IS THE WHOLE OF THIS FILE. Measured at
// 390×844 through the real route:
//
//     entry                n=0    n=43   n=100
//     Shops you could add  0.3    14.7   32.2    screens from the top
//     Or upload a feed     0.7    18.1   40.5
//     Advanced             1.6    19.0   41.4
//
// Everything except the list itself receded 0.40 of a phone screen for every
// shop the publisher added, so succeeding at the primary job made every other
// job harder to reach. An entry below a variable-length collection is not
// reachable at any account size — it is reachable at the account size you
// happened to test.
//
// The rule this encodes: A SCREEN MAY HOLD ONE VARIABLE-LENGTH COLLECTION, AND
// NOTHING ANOTHER INTENT MUST BE SCROLLED PAST. A link row and a `<details>` both
// satisfy that; a section below the list does not, whatever it contains.
//
// `Advanced` stays a native `<details>` — keyboard- and screen-reader-operable
// with no code, and its closed state really does keep the history rows out of
// the document — but it sits above the list now, at a constant depth.

const doorRow: React.CSSProperties = {
  display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 1rem',
};

const FeedsPage: React.FC = () => {
  // A shop's room destroys its own subject when it is deleted or removed, and
  // the two supply forms navigate away on success, so both hand the outcome over
  // in router state. Without this the publisher succeeds and is shown nothing.
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  const shopListRef = useRef<ShopListRef>(null);
  const feedListRef = useRef<FeedListRef>(null);
  const networkProductsRef = useRef<NetworkProductsManagerRef>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Only the diagnostics need telling: the shop list reloads itself, and the
  // forms that used to sit on this page now live on their own routes.
  const refreshDiagnostics = () => {
    feedListRef.current?.refresh();
    networkProductsRef.current?.refresh();
  };

  return (
    <div data-testid="feeds-section">
      <nav style={doorRow} aria-label="Add supply">
        <Link to="/feeds/add" className="btn btn--secondary" data-testid="door-add">Add a shop from your network</Link>
        <Link to="/feeds/upload" className="btn btn--secondary" data-testid="door-upload">Upload a feed file</Link>
        <Link to="/feeds/connect" className="btn btn--secondary" data-testid="door-connect">Connect a feed by URL</Link>
      </nav>

      <section className="feeds-section" style={{ marginBottom: '1.5rem' }}>
        <details
          data-testid="feeds-advanced"
          open={advancedOpen}
          onToggle={e => setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Advanced</summary>
          <p className="no-data" style={{ marginTop: '0.5rem' }}>
            The log of every processing run, and removing a whole network at once.
          </p>

          {/* Mounted only while open, so a publisher with hundreds of runs pays
              nothing for a panel they did not ask for. */}
          {advancedOpen && (
            <>
              <div style={{ marginTop: '1rem' }}>
                <FeedList ref={feedListRef} />
              </div>
              <NetworkProductsManager ref={networkProductsRef} onProductsDeleted={refreshDiagnostics} />
            </>
          )}
        </details>
      </section>

      <section className="feeds-section">
        <ShopList ref={shopListRef} notice={notice} />
      </section>
    </div>
  );
};

export default FeedsPage;
